// src/auth/use-role.ts
import { useAuthStore } from './auth-store';
import { isAlliedHealth, isDoctor, isNurse, mayOrder, mayStopOrSuspendPrescription } from './roles';
import { evaluatePermissions } from './permission-gate';
import type { UserData } from './types';

// The clinical personas (display/workflow-routing hints, NOT gates).
//
// The first five are the Encounter Contract §13a vocabulary, kept character-for-character in
// sync with the DS `ClinicalRole` (QuestionnaireRenderer's `role` prop) and with
// `encounter/lib/encounter-blocks.ts`.
//
// `'allied-health'` is PA-only (PROD-2040) and has NO counterpart in the DS union yet.
// Nothing currently feeds `clinicalRole` into a DS `role` prop, so this is safe today; if a
// future caller does, the DS union must gain the member first (or the value be narrowed at the
// boundary) rather than this one be collapsed back onto 'technician'.
//
// It is ONE member, not one per discipline. `'coach'` and `'nutritionist'` are the DS's own
// §13a vocabulary and stay as they are; they are NOT what an allied-health account resolves
// to. Which discipline an allied-health user practises — health coach, dietitian, exercise
// physiologist — is `speciality` DATA, not an access level, so it has no member here.
export type ClinicalRole =
  | 'physician'
  | 'nurse'
  | 'technician'
  | 'coach'
  | 'nutritionist'
  | 'allied-health';

// Order types AssessmentPlanCanvas can attach per diagnosis.
export type OrderType = 'lab' | 'imaging' | 'medication' | 'procedure' | 'referral' | 'vaccination';

// Every canvas order type this app can attach.
const ALL_ORDER_TYPES: OrderType[] = ['lab', 'imaging', 'medication', 'procedure', 'referral', 'vaccination'];

// The backend authorizes order placement with a SINGLE permission key — verified live against the
// dev permission model 2026-06-22: `Manage_Orders_Prescriptions`. There are NO per-order-type
// `Order_*` keys, and Flutter's A/P `orders_tab.dart` has no per-type gate (grep-confirmed; see the
// CLAUDE.md divergence register). So this is the real, data-driven gate — not invented per-type
// keys (those returned [] and disabled every order button in the PROD-1297 live smoke), not a
// hardcoded role table.
const ORDER_PERMISSION = 'Manage_Orders_Prescriptions';

/**
 * Derive allowed order types.
 *
 * FIRST the physician rule (PROD-2669): a user who is not on the physician allowlist
 * (`mayOrder`, {3, 8, 18}, pinned to Scrips.Common) gets NO order types, whatever their permission
 * data says — nurse, allied health, phlebotomist, technician, administrator and any unknown persona.
 * The server refuses those writes (Scrips.Patient `ClinicalWriteAuthorizationService`); this stops
 * the UI offering them.
 *
 * THEN, for a physician, the permission data — the real `Manage_Orders_Prescriptions` key, never a
 * per-type table. With the permission → all canvas order types are attachable; without it → none.
 * No permission data at all (an error/loading state) → don't disable a physician's orders
 * (Flutter's orders_tab has no gate), so the workflow isn't blocked.
 */
export function deriveAllowedOrderTypes(user: UserData | null | undefined): OrderType[] {
  if (!mayOrder(user)) return [];
  if (!user?.systemPermissions) return ALL_ORDER_TYPES;
  return evaluatePermissions(user.systemPermissions, [ORDER_PERMISSION], { add: true })
    ? ALL_ORDER_TYPES
    : [];
}

/**
 * Resolve the user's clinical persona.
 *
 * The allied-health branch keys on the TENANT ROLE ID ALONE (wire-coerced) — no `role` half, see
 * the header on `isAlliedHealth` in roles.ts for why the bucket move to `TenantId = 3` forces
 * that. Order is still irrelevant because the tenant ids are disjoint (nurse `'4'` vs allied
 * health `'20'`); it reads nurse-first only to mirror the pre-PROD-2040 code.
 *
 * The `'physician'` fallback is deliberate and unchanged: an unknown/absent tenant role id on a
 * doctor (or on any account we have no persona for) still resolves to 'physician', exactly as
 * before. What PROD-2040 removes is the KNOWN allied-health access level silently landing there.
 *
 * FOLLOW-UP, deliberately NOT done here: the allied-health DISCIPLINE (health coach / dietitian /
 * exercise physiologist) is `speciality`, and nothing can read it yet. `speciality` is not on
 * `UserData` at all — only `specialitySystem`, an unrelated string on the integration block —
 * and production returns the field EMPTY for staff accounts; it is populated for practitioners.
 * So the discipline needs a `UserData` field AND a backend fix before any surface can key off it.
 * Until then every allied-health account resolves to the one role and shows the one label.
 */
export function clinicalRoleFor(user: UserData | null | undefined): ClinicalRole {
  if (isNurse(user)) return 'nurse';
  if (isAlliedHealth(user)) return 'allied-health';
  return 'physician';
}

// Display labels, keyed exhaustively so a new ClinicalRole member cannot be added without one.
// The exhaustive `Record<ClinicalRole, _>` is the mechanism, not decoration: the original defect
// was an allied-health account falling through to 'physician' unnoticed, and this shape turns the
// next member added to the union into a compile error rather than a silent default.
const CLINICAL_ROLE_LABELS: Record<ClinicalRole, string> = {
  physician: 'Physician',
  nurse: 'Nurse',
  technician: 'Technician',
  coach: 'Health Coach',
  nutritionist: 'Dietitian',
  'allied-health': 'Allied Health',
};

/**
 * May this user sign AND LOCK an encounter?
 *
 * Sign-and-lock is an irreversible clinical finalisation, so it is a POSITIVE
 * capability: you must be recognisably a doctor (`ScripsRole = 3`) or on the
 * allied-health access level to get it.
 *
 * The previous shape was `!isNurse(user)` at the call site — the ABSENCE of a
 * nurse signal — which meant every way of failing to identify a nurse ended in a
 * LOCK:
 *   - a degraded profile (`GetUserDataToken` failed → `role: undefined`) locked;
 *   - an unresolved staff tenant role (`tenantRoleId` arrives as `''`) locked;
 *   - any future staff persona would lock by default.
 * Allied health is NOT in that list any more: it is granted the capability by the
 * explicit clause above, on the product ruling, not inherited by the absence of a
 * nurse signal.
 * Requiring the positive signal instead makes the failure direction "sign without
 * locking", which a doctor can still finalise afterwards, rather than "lock", which
 * nobody can undo.
 *
 * ## Why allied health is named EXPLICITLY rather than left to `isDoctor`
 *
 * The product ruling (2026-09-04) is that allied health MAY sign and lock their
 * own encounters. Once the tenant move lands, allied-health accounts sit on
 * `TenantId = 3` and `isDoctor(user)` is already true for them — so the ruling
 * would be satisfied BY ACCIDENT, as a side effect of which bucket the rows
 * happen to live in, with nothing in the code or the tests naming it.
 *
 * That is the failure mode this `|| isAlliedHealth(user)` exists to prevent. The
 * moment anyone narrows `isDoctor` — to exclude a sub-bucket, to key on a real
 * practitioner flag, to split the "Doctor" bucket the way allied health was just
 * merged into it — allied health would SILENTLY lose the capability, and no test
 * would go red, because no test would be about them. Naming the grant here makes
 * it a guarantee with an owner and a test (`use-role.test.ts`), not an artefact
 * of a data migration. The clause is redundant today on purpose.
 *
 * Defence in depth, not a duplicate of the token-service fix in this same branch.
 * That fix stops a FAILED profile fetch from becoming a session at all; this one
 * still matters because a session can carry no role without any fetch failing — a
 * profile cached by an older build and re-hydrated on boot, or a 200 whose body
 * simply omits the field.
 *
 * For the population that exists today — doctors (`role '3'`) and nurses
 * (`role '2'` + `tenantRoleId '4'`) — this is behaviourally IDENTICAL to
 * `!isNurse(user)`. Asserted, not assumed: the behaviour-change register at the
 * bottom of `src/encounter/lib/to-sign-lock.test.ts` checks the agreement for both
 * live personas and enumerates the cases that used to lock and now do not.
 *
 * FOLLOW-UP (not done here): this should become permission-driven, the way
 * `deriveAllowedOrderTypes` above already is — a real key off
 * `user.systemPermissions`, never a role table. It is left as a role check
 * because no verified sign/lock permission key is known to exist in the backend
 * permission model; inventing one is how `Order_*` disabled every order button
 * in the PROD-1297 live smoke. Confirm the key against the live permission model
 * first, then move this over.
 */
export function canLockEncounter(user: UserData | null | undefined): boolean {
  return isDoctor(user) || isAlliedHealth(user);
}

export function roleLabelFor(user: UserData | null | undefined): string {
  return CLINICAL_ROLE_LABELS[clinicalRoleFor(user)];
}

export interface RoleContext {
  user: UserData | null;
  isNurse: boolean;
  /** Positive capability to sign AND lock an encounter — see canLockEncounter. */
  canLock: boolean;
  clinicalRole: ClinicalRole;
  roleLabel: string;
  allowedOrderTypes: OrderType[];
  /** PROD-2669: on the physician allowlist, so may order (see `mayOrder` in roles.ts). */
  mayOrder: boolean;
  /** PROD-2669: may stop or suspend a prescription (physicians only). */
  mayStopOrSuspend: boolean;
}

export function useRole(): RoleContext {
  const user = useAuthStore((s) => s.user);
  // `isNurse` deliberately still calls the nurse gate directly rather than reading
  // `clinicalRole === 'nurse'` — same result, but it keeps the nurse gate's single call site
  // pointing at the one definition #371 consolidated it into.
  const nurse = isNurse(user);
  const clinicalRole = clinicalRoleFor(user);
  return {
    user,
    isNurse: nurse,
    canLock: canLockEncounter(user),
    clinicalRole,
    roleLabel: CLINICAL_ROLE_LABELS[clinicalRole],
    allowedOrderTypes: deriveAllowedOrderTypes(user),
    mayOrder: mayOrder(user),
    mayStopOrSuspend: mayStopOrSuspendPrescription(user),
  };
}
